import {
  doc,
  getDocFromServer,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  writeBatch,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { normalizeRoomCode } from '../lib/domain'
import type { Game, GameAction, Member, Player } from '../types'
import {
  REMOTE_HARNESS_SCHEMA_VERSION,
  type RemoteCheckCommand,
  type RemoteHarnessLegalAction,
  type RemoteHarnessState,
  type RemoteHarnessStateWrite,
  type RemoteHoleCards,
  type RemoteHoleCardsWrite,
} from './remoteTypes'

const CARD_CODE = /^[2-9TJQKA][cdhs]$/
const SAFE_IDENTIFIER = /^[A-Za-z0-9_-]+$/
const REMOTE_ACTIONS: readonly RemoteHarnessLegalAction[] = ['fold', 'check', 'call', 'bet', 'raise']
const REMOTE_PHASES: readonly RemoteHarnessState['phase'][] = [
  'waiting', 'posting_blinds', 'betting', 'awaiting_street',
  'awaiting_award', 'awaiting_next_hand', 'paused',
]
const REMOTE_STREETS: readonly RemoteHarnessState['street'][] = ['preflop', 'flop', 'turn', 'river', 'showdown']

export type RemoteSnapshotHandler<T> = (value: T | null, fromCache: boolean) => void

function roomCode(code: string): string {
  const normalized = normalizeRoomCode(code)
  if (normalized.length !== 4) throw new Error('Remote harness room code must contain 4 characters.')
  return normalized
}

function assertIdentifier(value: string, label: string, allowEmpty = false): void {
  if (allowEmpty && value === '') return
  if (!SAFE_IDENTIFIER.test(value) || value.length > 128) {
    throw new Error(`${label} must be a safe identifier of at most 128 characters.`)
  }
}

function assertWholeNumber(value: number, label: string, minimum = 0): void {
  if (!Number.isSafeInteger(value) || value < minimum) {
    throw new Error(`${label} must be a whole number of at least ${minimum}.`)
  }
}

function assertCardCodes(cards: readonly string[], label: string): void {
  if (cards.some((card) => !CARD_CODE.test(card))) {
    throw new Error(`${label} contains an invalid card code.`)
  }
  if (new Set(cards).size !== cards.length) {
    throw new Error(`${label} contains a duplicate card.`)
  }
}

function expectedBoardSize(street: RemoteHarnessState['street']): number {
  if (street === 'preflop') return 0
  if (street === 'flop') return 3
  if (street === 'turn') return 4
  return 5
}

/**
 * Rebuilds the public projection field-by-field. Apart from validating the
 * contract, this prevents an accidental spread of a seed, deck, or hole cards
 * supplied by a wider runtime object.
 */
export function sanitizeRemoteHarnessState(input: RemoteHarnessStateWrite): RemoteHarnessStateWrite {
  if (input.schemaVersion !== REMOTE_HARNESS_SCHEMA_VERSION) throw new Error('Unsupported remote harness schema version.')
  if (input.mode !== 'two-human' || input.expectedPlayers !== 2) throw new Error('Remote harness state must describe exactly two human players.')
  assertIdentifier(input.runId, 'Run ID')
  assertWholeNumber(input.handNumber, 'Hand number', 1)
  if (!REMOTE_PHASES.includes(input.phase)) throw new Error('Invalid remote harness phase.')
  if (!REMOTE_STREETS.includes(input.street)) throw new Error('Invalid remote harness street.')
  assertIdentifier(input.dealerUid, 'Dealer UID', true)
  assertIdentifier(input.smallBlindUid, 'Small-blind UID', true)
  assertIdentifier(input.bigBlindUid, 'Big-blind UID', true)
  assertIdentifier(input.actorUid, 'Actor UID', true)
  assertIdentifier(input.turnToken, 'Turn token', true)
  assertIdentifier(input.processedActionId, 'Processed action ID', true)
  assertWholeNumber(input.expectedPot, 'Expected pot')
  assertWholeNumber(input.revision, 'Revision')

  if (input.board.length !== expectedBoardSize(input.street)) {
    throw new Error(`${input.street} must expose exactly ${expectedBoardSize(input.street)} community cards.`)
  }
  assertCardCodes(input.board, 'Public board')

  const legalActions = [...input.prompt.legalActions]
  if (new Set(legalActions).size !== legalActions.length || legalActions.some((action) => !REMOTE_ACTIONS.includes(action))) {
    throw new Error('Prompt contains an invalid or duplicate legal action.')
  }
  assertWholeNumber(input.prompt.toCall, 'Call amount')

  if (input.canCheck) {
    if (input.phase !== 'betting' || !input.actorUid || !input.turnToken || !legalActions.includes('check')) {
      throw new Error('A checkable turn needs a betting actor, turn token, and Check legal action.')
    }
  }
  if (!input.actorUid && (input.turnToken || input.canCheck)) {
    throw new Error('A turn token or Check permission cannot exist without an actor.')
  }

  if (input.requiredBlind) {
    assertIdentifier(input.requiredBlind.playerUid, 'Required-blind player UID')
    if (input.requiredBlind.kind !== 'small' && input.requiredBlind.kind !== 'big') throw new Error('Invalid required blind kind.')
    assertWholeNumber(input.requiredBlind.amount, 'Required blind', 1)
  }
  if (input.expectedWinnerUid !== null) assertIdentifier(input.expectedWinnerUid, 'Expected winner UID')

  return {
    schemaVersion: REMOTE_HARNESS_SCHEMA_VERSION,
    mode: 'two-human',
    expectedPlayers: 2,
    runId: input.runId,
    handNumber: input.handNumber,
    phase: input.phase,
    street: input.street,
    dealerUid: input.dealerUid,
    smallBlindUid: input.smallBlindUid,
    bigBlindUid: input.bigBlindUid,
    actorUid: input.actorUid,
    turnToken: input.turnToken,
    canCheck: input.canCheck,
    requiredBlind: input.requiredBlind
      ? { playerUid: input.requiredBlind.playerUid, kind: input.requiredBlind.kind, amount: input.requiredBlind.amount }
      : null,
    board: [...input.board],
    prompt: {
      headline: input.prompt.headline.slice(0, 120),
      detail: input.prompt.detail.slice(0, 360),
      toCall: input.prompt.toCall,
      legalActions,
    },
    expectedWinnerUid: input.expectedWinnerUid,
    expectedPot: input.expectedPot,
    processedActionId: input.processedActionId,
    revision: input.revision,
  }
}

export function sanitizeRemoteHoleCards(input: RemoteHoleCardsWrite): RemoteHoleCardsWrite {
  assertIdentifier(input.uid, 'Player UID')
  assertIdentifier(input.runId, 'Run ID')
  assertWholeNumber(input.handNumber, 'Hand number', 1)
  if (input.cards.length !== 2) throw new Error('A remote hand must contain exactly two hole cards.')
  assertCardCodes(input.cards, 'Hole cards')
  return {
    uid: input.uid,
    runId: input.runId,
    handNumber: input.handNumber,
    cards: [input.cards[0], input.cards[1]],
  }
}

export function remoteHarnessStateRef(db: Firestore, code: string) {
  return doc(db, 'games', roomCode(code), 'harness', 'state')
}

export function remoteHoleCardsRef(db: Firestore, code: string, uid: string) {
  assertIdentifier(uid, 'Player UID')
  return doc(db, 'games', roomCode(code), 'harnessHands', uid)
}

function remoteGameRef(db: Firestore, code: string) {
  return doc(db, 'games', roomCode(code))
}

function remoteMemberRef(db: Firestore, code: string, uid: string) {
  return doc(db, 'games', roomCode(code), 'members', uid)
}

function remotePlayerRef(db: Firestore, code: string, uid: string) {
  return doc(db, 'games', roomCode(code), 'players', uid)
}

export function remoteCheckActionId(turnToken: string, uid: string): string {
  assertIdentifier(turnToken, 'Turn token')
  assertIdentifier(uid, 'Player UID')
  return `remote-check-${turnToken}-${uid}`
}

function remoteCheckActionRef(db: Firestore, code: string, uid: string, turnToken: string) {
  return doc(db, 'games', roomCode(code), 'actions', remoteCheckActionId(turnToken, uid))
}

export async function publishRemoteHarnessState(
  db: Firestore,
  code: string,
  state: RemoteHarnessStateWrite,
): Promise<void> {
  const value = sanitizeRemoteHarnessState(state)
  await setDoc(remoteHarnessStateRef(db, code), { ...value, updatedAt: serverTimestamp() })
}

export async function publishRemoteHoleCards(
  db: Firestore,
  code: string,
  hands: readonly RemoteHoleCardsWrite[],
): Promise<void> {
  if (hands.length < 1 || hands.length > 2) throw new Error('Publish one or two remote player hands at a time.')
  const values = hands.map(sanitizeRemoteHoleCards)
  if (new Set(values.map((hand) => hand.uid)).size !== values.length) throw new Error('Remote player hands must have unique UIDs.')
  const allCards = values.flatMap((hand) => hand.cards)
  assertCardCodes(allCards, 'Remote player hands')

  const batch = writeBatch(db)
  values.forEach((hand) => batch.set(remoteHoleCardsRef(db, code, hand.uid), {
    ...hand,
    updatedAt: serverTimestamp(),
  }))
  await batch.commit()
}

/** Atomically advances the public projection and both private phone hands. */
export async function publishRemoteHarnessDeal(
  db: Firestore,
  code: string,
  state: RemoteHarnessStateWrite,
  hands: readonly [RemoteHoleCardsWrite, RemoteHoleCardsWrite],
): Promise<void> {
  const publicValue = sanitizeRemoteHarnessState(state)
  const privateValues = hands.map(sanitizeRemoteHoleCards) as [RemoteHoleCardsWrite, RemoteHoleCardsWrite]
  if (privateValues[0].uid === privateValues[1].uid) throw new Error('Remote player hands must have unique UIDs.')
  if (privateValues.some((hand) => hand.runId !== publicValue.runId || hand.handNumber !== publicValue.handNumber)) {
    throw new Error('Public state and private hands must belong to the same run and hand.')
  }
  assertCardCodes(privateValues.flatMap((hand) => hand.cards), 'Remote player hands')

  const batch = writeBatch(db)
  batch.set(remoteHarnessStateRef(db, code), { ...publicValue, updatedAt: serverTimestamp() })
  privateValues.forEach((hand) => batch.set(remoteHoleCardsRef(db, code, hand.uid), {
    ...hand,
    updatedAt: serverTimestamp(),
  }))
  await batch.commit()
}

export function listenToRemoteHarnessState(
  db: Firestore,
  code: string,
  onValue: RemoteSnapshotHandler<RemoteHarnessState>,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    remoteHarnessStateRef(db, code),
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.exists() ? snapshot.data() as RemoteHarnessState : null, snapshot.metadata.fromCache),
    onError,
  )
}

export function listenToRemoteHoleCards(
  db: Firestore,
  code: string,
  uid: string,
  onValue: RemoteSnapshotHandler<RemoteHoleCards>,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    remoteHoleCardsRef(db, code, uid),
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.exists() ? snapshot.data() as RemoteHoleCards : null, snapshot.metadata.fromCache),
    onError,
  )
}

export async function readRemoteHarnessStateFromServer(db: Firestore, code: string): Promise<RemoteHarnessState | null> {
  const snapshot = await getDocFromServer(remoteHarnessStateRef(db, code))
  return snapshot.exists() ? snapshot.data() as RemoteHarnessState : null
}

export async function readRemoteHoleCardsFromServer(db: Firestore, code: string, uid: string): Promise<RemoteHoleCards | null> {
  const snapshot = await getDocFromServer(remoteHoleCardsRef(db, code, uid))
  return snapshot.exists() ? snapshot.data() as RemoteHoleCards : null
}

/**
 * Records Check in the same immutable action log and room revision used by
 * bets/folds. `processedActionId` ties the public turn projection to the exact
 * core revision, so a stale Check cannot commit after another phone action.
 */
export async function recordHarnessCheck(
  db: Firestore,
  code: string,
  uid: string,
  turnToken: string,
): Promise<RemoteCheckCommand> {
  assertIdentifier(uid, 'Player UID')
  assertIdentifier(turnToken, 'Turn token')
  const normalizedCode = roomCode(code)
  const gameReference = remoteGameRef(db, normalizedCode)
  const memberReference = remoteMemberRef(db, normalizedCode, uid)
  const playerReference = remotePlayerRef(db, normalizedCode, uid)
  const stateReference = remoteHarnessStateRef(db, normalizedCode)
  const actionReference = remoteCheckActionRef(db, normalizedCode, uid, turnToken)

  return runTransaction(db, async (transaction) => {
    const [gameSnapshot, memberSnapshot, playerSnapshot, stateSnapshot, existingActionSnapshot] = await Promise.all([
      transaction.get(gameReference),
      transaction.get(memberReference),
      transaction.get(playerReference),
      transaction.get(stateReference),
      transaction.get(actionReference),
    ])

    if (existingActionSnapshot.exists()) {
      const existing = { id: existingActionSnapshot.id, ...existingActionSnapshot.data() } as GameAction
      if (existing.type !== 'check'
        || existing.actorUid !== uid
        || existing.harnessTurnToken !== turnToken) {
        throw new Error('The Check operation ID is already used by another action.')
      }
      return {
        id: existing.id,
        type: 'check',
        actorUid: existing.actorUid,
        handNumber: existing.handNumber,
        turnToken,
        createdAt: existing.createdAt,
      }
    }

    if (!gameSnapshot.exists() || !memberSnapshot.exists() || !playerSnapshot.exists() || !stateSnapshot.exists()) {
      throw new Error('The remote test is not ready for Check.')
    }

    const game = gameSnapshot.data() as Game
    const member = memberSnapshot.data() as Member
    const player = playerSnapshot.data() as Player
    const state = stateSnapshot.data() as RemoteHarnessState
    if ((game.phase ?? 'playing') !== 'playing' || game.handOpen === false) throw new Error('The current hand is not open.')
    if (!member.isPlayer || member.uid !== uid || player.uid !== uid) throw new Error('Only the active player may Check.')
    if (player.foldedHand === game.handNumber) throw new Error('A folded player cannot Check.')
    if (state.mode !== 'two-human'
      || state.phase !== 'betting'
      || state.handNumber !== game.handNumber
      || state.actorUid !== uid
      || state.turnToken !== turnToken
      || !state.canCheck
      || !state.prompt.legalActions.includes('check')) {
      throw new Error('Check is not legal for this player now.')
    }
    if (state.processedActionId !== game.lastActionId) {
      throw new Error('The remote turn changed; wait for it to refresh before checking.')
    }

    transaction.update(gameReference, {
      lastActionId: actionReference.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(actionReference, {
      type: 'check',
      actorUid: uid,
      actorName: member.displayName,
      targetUid: uid,
      targetName: player.name,
      amount: 0,
      handNumber: game.handNumber,
      reversesActionId: '',
      harnessTurnToken: turnToken,
      createdAt: serverTimestamp(),
    })

    return {
      id: actionReference.id,
      type: 'check',
      actorUid: uid,
      handNumber: game.handNumber,
      turnToken,
      createdAt: null,
    }
  })
}
